import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiGet, apiPost } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";
import {
  ProductEditorFormFields,
  defaultProductForm
} from "./ProductEditorFormFields";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function NewProductPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [form, setForm] = useState({
    ...defaultProductForm,
    customerId: isAdmin ? "" : String(user?.customerId ?? "")
  });

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    })();
  }, [isAdmin]);

  const loadLookups = useCallback(async (customerId) => {
    if (!customerId) {
      setCategories([]);
      setBrands([]);
      return;
    }
    const q = `customerId=${encodeURIComponent(customerId)}`;
    const [catRes, brandRes] = await Promise.all([
      apiGet(`/api/ProductCategories?${q}`),
      apiGet(`/api/Brands?${q}`)
    ]);
    setCategories(catRes.ok && Array.isArray(catRes.data) ? catRes.data : []);
    setBrands(brandRes.ok && Array.isArray(brandRes.data) ? brandRes.data : []);
  }, []);

  useEffect(() => {
    const cid = form.customerId || user?.customerId;
    if (cid) void loadLookups(cid);
  }, [form.customerId, user?.customerId, loadLookups]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const customerId = isAdmin
      ? Number(form.customerId || 0)
      : Number(user?.customerId || 0);
    if (!customerId) {
      toast.push("Customer is required", "error");
      return;
    }
    if (!String(form.name || "").trim()) {
      toast.push("Name is required", "error");
      return;
    }
    if (!Number(form.categoryId)) {
      toast.push("Category is required", "error");
      return;
    }

    const discountRaw = String(form.discountPrice ?? "").trim();
    const payload = {
      customerId,
      code: String(form.code || "").trim() || null,
      barcode: String(form.barcode || "").trim() || null,
      name: String(form.name || "").trim(),
      subtitle: String(form.subtitle || "").trim() || null,
      shortDescription: String(form.shortDescription || "").trim() || null,
      description: String(form.description || "").trim() || null,
      content: form.content || null,
      detailContentMode: form.detailContentMode === "json" ? "json" : "html",
      sectionsJson: String(form.sectionsJson || "").trim() || null,
      categoryId: Number(form.categoryId),
      brandId: form.brandId ? Number(form.brandId) : null,
      unitName: String(form.unitName || "").trim() || null,
      price: toNumber(form.price, 0),
      discountPrice: discountRaw === "" ? null : toNumber(discountRaw, 0),
      minQuantity: Math.max(1, toNumber(form.minQuantity, 1)),
      image: String(form.image || "").trim() || null,
      order: toNumber(form.order, 0),
      images: (form.images || [])
        .filter((x) => x.image)
        .map((x, index) => ({ image: x.image, order: index }))
    };

    setSaving(true);
    const res = await apiPost("/api/Products", payload);
    setSaving(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to create product"), "error");
      return;
    }
    toast.push("Product created", "success");
    navigate("/products");
  };

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Add product</h1>
            <p className="mt-1 text-sm text-slate-500">
              Catalog details plus optional landing page content.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              type="submit"
              form="new-product-form"
              disabled={saving}
              className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Create product"}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="new-product-form" onSubmit={onSubmit}>
          <ProductEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            categories={categories}
            brands={brands}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
          />
        </form>
      </div>
    </div>
  );
}
